import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { scansAPI, findingsAPI, changeDetectionAPI } from '../services/api';
import socketService from '../services/socket';
import DeveloperFindingDetails from '../components/findings/DeveloperFindingDetails';
import { formatDuration } from '../utils/format';
import './ScanDetail.css';

function ScanDetail() {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [findings, setFindings] = useState([]);
  const [isTrialSummary, setIsTrialSummary] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [filters, setFilters] = useState({
    severity: '',
    status: '',
    type: '',
  });

  // Live Progress State
  const [progress, setProgress] = useState(null);
  const [liveDuration, setLiveDuration] = useState(0);

  useEffect(() => {
    loadScanData();
  }, [scanId, filters]);

  useEffect(() => {
    if (activeTab === 'timeline') {
      loadTimeline();
    }
  }, [scanId, activeTab]);

  // Live Timer for Running Scans
  useEffect(() => {
    let timer;
    if (scan?.status === 'running' || scan?.status === 'pending') {
      const startTime = new Date(scan.created_at).getTime();
      timer = setInterval(() => {
        const now = new Date().getTime();
        setLiveDuration(Math.floor((now - startTime) / 1000));
      }, 1000);
    } else if (scan?.duration) {
      setLiveDuration(scan.duration);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [scan?.status, scan?.created_at, scan?.duration]);

  // WebSocket for real-time updates
  useEffect(() => {
    socketService.connect();
    socketService.joinScan(scanId);

    socketService.on('scan_progress', (data) => {
      if (data.scan_id === parseInt(scanId)) {
        setProgress({
          state: 'PROGRESS',
          current: data.current,
          total: data.total,
          status: data.status
        });
        setScan(prevScan => ({ ...prevScan, status: 'running' }));
      }
    });

    socketService.on('scan_completed', (data) => {
      if (data.scan_id === parseInt(scanId)) {
        setProgress({ state: 'SUCCESS', status: 'Scan completed successfully' });
        setScan(prevScan => ({ ...prevScan, status: 'completed', duration: data.duration }));
        loadScanData();
      }
    });

    socketService.on('scan_failed', (data) => {
      if (data.scan_id === parseInt(scanId)) {
        setProgress({ state: 'FAILURE', status: 'Scan failed', error: data.error });
        setScan(prevScan => ({ ...prevScan, status: 'failed' }));
      }
    });

    return () => {
      socketService.off('scan_progress');
      socketService.off('scan_completed');
      socketService.off('scan_failed');
    };
  }, [scanId]);

  const loadScanData = async () => {
    try {
      const [scanResponse, findingsResponse] = await Promise.all([
        scansAPI.get(scanId),
        scansAPI.getFindings(scanId, filters),
      ]);
      setScan(scanResponse.data.scan);
      setIsTrialSummary(!!scanResponse.data.scan.trial_summary_only);
      setFindings(findingsResponse.data);
    } catch (error) {
      console.error('Error loading scan data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTimeline = async () => {
    setTimelineLoading(true);
    try {
      const response = await changeDetectionAPI.list(scanId);
      const timelineEntries = response.data.timeline || response.data || [];
      setTimeline(timelineEntries);
    } catch (error) {
      console.error('Error loading exposure timeline:', error);
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleStatusChange = async (findingId, newStatus) => {
    try {
      await findingsAPI.updateStatus(findingId, newStatus);
      loadScanData();
    } catch (error) {
      console.error('Error updating finding status:', error);
    }
  };

  const linkify = (text) => {
    if (!text) return text;
    const urlRegex = /(https?:\/\/[^\s<>"]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, i) => {
      if (part && part.match(/^https?:\/\//)) {
        return (
          <a 
            key={i} 
            href={part} 
            target="_blank" 
            rel="noopener noreferrer"
            className="linkified"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this scan and all its findings?')) {
      try {
        await scansAPI.delete(scanId);
        navigate('/scans');
      } catch (error) {
        console.error('Error deleting scan:', error);
        alert('Failed to delete scan.');
      }
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const response = await scansAPI.exportScan(scanId, 'pdf');
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PassiveGuard_Scan_${scanId}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to generate PDF report.');
    } finally {
      setExporting(false);
    }
  };

  const formatTimelineWindow = (entry) => {
    if (entry.previous_scan_id && entry.current_scan_id) {
      return `Scan ${entry.previous_scan_id} → ${entry.current_scan_id}`;
    }
    if (entry.range_label) return entry.range_label;
    if (entry.detected_at) {
      return new Date(entry.detected_at).toLocaleString();
    }
    return 'Recent scan window';
  };

  // ========== NEW:  Network Finding Parser ==========
  const parseNetworkFinding = (finding) => {
    // Check if this is a network-related finding
    const networkTypes = [
      'dns_exfiltration', 'dns_tunneling', 'nxdomain_burst',
      'port_scan', 'brute_force_attempt', 'c2_beaconing',
      'flow_anomaly', 'insecure_protocol', 'plaintext_credential'
    ];

    if (! networkTypes.includes(finding.vulnerability_type)) {
      return null; // Not a network finding
    }

    // Try to parse data_flow_trace as JSON for structured network data
    let networkData = null;
    try {
      if (finding.data_flow_trace) {
        networkData = JSON.parse(finding.data_flow_trace);
      }
    } catch (e) {
      // If not JSON, it's just text - that's fine
    }

    return {
      isNetwork: true,
      type: finding.vulnerability_type,
      data: networkData,
      rawTrace: finding.data_flow_trace
    };
  };

  // ========== NEW: Network Finding Renderer ==========
  const renderNetworkFinding = (finding, networkInfo) => {
    const { type, data } = networkInfo;

    switch (type) {
      case 'dns_exfiltration': 
      case 'dns_tunneling': 
        return (
          <div className="network-finding-details dns-finding">
            <h4>🚨 DNS Security Alert</h4>
            <div className="network-detail-grid">
              {data?.domain && (
                <div className="network-detail-item">
                  <span className="network-label">Suspicious Domain:</span>
                  <code className="network-value">{data.domain}</code>
                </div>
              )}
              {data?.query_count && (
                <div className="network-detail-item">
                  <span className="network-label">Query Count:</span>
                  <span className="network-value badge badge-high">{data.query_count}</span>
                </div>
              )}
              {data?.entropy && (
                <div className="network-detail-item">
                  <span className="network-label">Entropy Score:</span>
                  <span className="network-value">
                    {typeof data.entropy === 'number' ? data.entropy.toFixed(2) : data.entropy}
                  </span>
                </div>
              )}
              {data?.pattern && (
                <div className="network-detail-item">
                  <span className="network-label">Detected Pattern:</span>
                  <code className="network-value">{data.pattern}</code>
                </div>
              )}
            </div>
          </div>
        );

      case 'port_scan':
        return (
          <div className="network-finding-details port-scan-finding">
            <h4>🔍 Port Scan Detection</h4>
            <div className="network-detail-grid">
              {data?.source_ip && (
                <div className="network-detail-item">
                  <span className="network-label">Source IP:</span>
                  <code className="network-value">{data.source_ip}</code>
                </div>
              )}
              {data?.target_ports && (
                <div className="network-detail-item">
                  <span className="network-label">Targeted Ports:</span>
                  <code className="network-value">{Array.isArray(data.target_ports) ? data.target_ports.join(', ') : data.target_ports}</code>
                </div>
              )}
              {data?.scan_type && (
                <div className="network-detail-item">
                  <span className="network-label">Scan Type:</span>
                  <span className="badge badge-medium">{data.scan_type}</span>
                </div>
              )}
              {data?.duration && (
                <div className="network-detail-item">
                  <span className="network-label">Duration:</span>
                  <span className="network-value">{data.duration}</span>
                </div>
              )}
            </div>
          </div>
        );

      case 'c2_beaconing':
        return (
          <div className="network-finding-details c2-finding">
            <h4>⚠️ Command & Control Activity</h4>
            <div className="network-detail-grid">
              {data?.destination && (
                <div className="network-detail-item">
                  <span className="network-label">C2 Server:</span>
                  <code className="network-value">{data.destination}</code>
                </div>
              )}
              {data?.beacon_interval && (
                <div className="network-detail-item">
                  <span className="network-label">Beacon Interval:</span>
                  <span className="network-value">{data.beacon_interval}</span>
                </div>
              )}
              {data?.regularity_score && (
                <div className="network-detail-item">
                  <span className="network-label">Regularity Score:</span>
                  <span className="network-value badge badge-high">{data.regularity_score.toFixed(2)}</span>
                </div>
              )}
              {data?.packet_count && (
                <div className="network-detail-item">
                  <span className="network-label">Packet Count:</span>
                  <span className="network-value">{data.packet_count}</span>
                </div>
              )}
            </div>
          </div>
        );

      case 'insecure_protocol':
        return (
          <div className="network-finding-details protocol-finding">
            <h4>🔓 Insecure Protocol Detected</h4>
            <div className="network-detail-grid">
              {data?.protocol && (
                <div className="network-detail-item">
                  <span className="network-label">Protocol:</span>
                  <span className="badge badge-medium">{data.protocol}</span>
                </div>
              )}
              {data?.version && (
                <div className="network-detail-item">
                  <span className="network-label">Version:</span>
                  <code className="network-value">{data.version}</code>
                </div>
              )}
              {data?.port && (
                <div className="network-detail-item">
                  <span className="network-label">Port:</span>
                  <span className="network-value">{data.port}</span>
                </div>
              )}
            </div>
          </div>
        );

      case 'plaintext_credential':
        return (
          <div className="network-finding-details credential-finding">
            <h4>🔑 Plaintext Credential Exposure</h4>
            <div className="network-detail-grid">
              {data?.protocol && (
                <div className="network-detail-item">
                  <span className="network-label">Protocol:</span>
                  <span className="badge badge-high">{data.protocol}</span>
                </div>
              )}
              {data?.credential_type && (
                <div className="network-detail-item">
                  <span className="network-label">Credential Type:</span>
                  <span className="network-value">{data.credential_type}</span>
                </div>
              )}
              {data?.packet_number && (
                <div className="network-detail-item">
                  <span className="network-label">Packet #:</span>
                  <span className="network-value">{data.packet_number}</span>
                </div>
              )}
            </div>
            <div className="security-notice">
              <strong>⚠️ Security Notice:</strong> Credentials were transmitted in cleartext and are visible to network attackers.
            </div>
          </div>
        );

      case 'brute_force_attempt':
        return (
          <div className="network-finding-details brute-force-finding">
            <h4>🔨 Brute Force Attack Detected</h4>
            <div className="network-detail-grid">
              {data?.source_ip && (
                <div className="network-detail-item">
                  <span className="network-label">Attacker IP:</span>
                  <code className="network-value">{data.source_ip}</code>
                </div>
              )}
              {data?.attempt_count && (
                <div className="network-detail-item">
                  <span className="network-label">Attempts:</span>
                  <span className="network-value badge badge-high">{data.attempt_count}</span>
                </div>
              )}
              {data?.target_service && (
                <div className="network-detail-item">
                  <span className="network-label">Target Service:</span>
                  <span className="badge badge-medium">{data.target_service}</span>
                </div>
              )}
              {data?.timeframe && (
                <div className="network-detail-item">
                  <span className="network-label">Timeframe:</span>
                  <span className="network-value">{data.timeframe}</span>
                </div>
              )}
            </div>
          </div>
        );

      default:
        // Generic network finding display
        return (
          <div className="network-finding-details generic-network">
            <h4>🌐 Network Anomaly Detected</h4>
            {data && (
              <div className="network-detail-grid">
                {Object.entries(data).map(([key, value]) => (
                  <div key={key} className="network-detail-item">
                    <span className="network-label">{key.replace(/_/g, ' ').toUpperCase()}:</span>
                    <span className="network-value">{JSON.stringify(value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
    }
  };

  if (loading) {
    return <div className="spinner" />;
  }

  if (!scan) {
    return <div>Scan not found</div>;
  }

  return (
    <div className="container theme-github">
      <div className="gh-repo-header" style={{ marginBottom: '20px' }}>
        <div className="gh-repo-title">
          <svg className="gh-icon" viewBox="0 0 16 16" width="16" height="16"><path fillRule="evenodd" d="M2 2.5A2.5 2.5 0 014.5 0h8.75a.75.75 0 01.75.75v12.5a.75.75 0 01-.75.75h-2.5a.75.75 0 110-1.5h1.75v-2h-8a1 1 0 00-.714 1.7L5 12.865a.5.5 0 11-.706.708l-1.611-1.611A2.503 2.503 0 012 10.25V2.5zM4.5 1.5a1 1 0 00-1 1V10.25c0 .41.16.806.44 1.104l.843.843L6.34 10.64a1 1 0 01.78-.39h7.38v-8.75a1 1 0 00-1-1h-9z"></path></svg>
          <span className="repo-owner">Scans</span>
          <span className="path-divider">/</span>
          <span className="repo-name">{scan ? (scan.name || scan.id) : 'Loading...'}</span>
          <span className={`gh-badge gh-badge-${scan?.status === 'completed' ? 'success' : scan?.status === 'failed' ? 'danger' : 'warning'}`} style={{ marginLeft: '10px' }}>
            {scan?.status || 'Unknown'}
          </span>
        </div>
        <div className="gh-repo-actions">
          <button 
            className="gh-btn" 
            onClick={handleExportPDF} 
            disabled={exporting || scan?.status === 'running' || scan?.status === 'pending'}
          >
            {exporting ? 'Generating PDF...' : 'Download PDF'}
          </button>
          <button className="gh-btn gh-btn-danger" onClick={handleDelete}>
            Delete
          </button>
          <button className="gh-btn" onClick={loadScanData} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      <div className="gh-tabs">
        <button 
          className={`gh-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <svg className="gh-icon" viewBox="0 0 16 16" width="16" height="16"><path fillRule="evenodd" d="M0 8a8 8 0 1116 0A8 8 0 010 8zm8-6.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13z"></path></svg>
          Overview
        </button>
        <button 
          className={`gh-tab ${activeTab === 'findings' ? 'active' : ''}`}
          onClick={() => setActiveTab('findings')}
        >
          <svg className="gh-icon" viewBox="0 0 16 16" width="16" height="16"><path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm9 3a1 1 0 11-2 0 1 1 0 012 0zm-.25-6.25a.75.75 0 00-1.5 0v3.5a.75.75 0 001.5 0v-3.5z"></path></svg>
          Findings
          <span className="gh-counter">{findings.length}</span>
        </button>
        <button 
          className={`gh-tab ${activeTab === 'timeline' ? 'active' : ''}`}
          onClick={() => setActiveTab('timeline')}
        >
          <svg className="gh-icon" viewBox="0 0 16 16" width="16" height="16"><path fillRule="evenodd" d="M11 1.5l-9 11 2 2 9-11-2-2zm-1 0l-9 11 2 2 9-11-2-2zM1.5 8a6.5 6.5 0 1113 0 6.5 6.5 0 01-13 0zM8 0a8 8 0 100 16A8 8 0 008 0zm.5 4.75a.75.75 0 00-1.5 0v3.5a.75.75 0 00.387.657l2.5 1.5a.75.75 0 00.75-1.3l2.113-1.268V4.75z"></path></svg>
          Timeline
        </button>
      </div>

      <div className="gh-content">
        {activeTab === 'overview' && (
          <div className="gh-panel">
            <div className="gh-panel-header">
              <h3 className="gh-panel-title">Scan Metadata & Live Status</h3>
            </div>
            <div className="gh-panel-body">
              {(scan?.status === 'pending' || scan?.status === 'running') && (
                <div className="gh-progress-block" style={{ marginBottom: '30px' }}>
                  <div className="progress-container">
                    <div 
                      className="progress-bar" 
                      style={{ width: `${((progress?.current || 0) / (progress?.total || 6)) * 100}%` }}
                    ></div>
                  </div>
                  <p className="progress-status" style={{ marginTop: '10px' }}>
                    <span className="spinner-small"></span> {progress?.status || 'Processing...'}
                  </p>
                </div>
              )}

              <div className="scan-info-grid">
                <div className="gh-info-card">
                  <h4>Configuration</h4>
                  <p><strong>Target:</strong> {scan?.target_path}</p>
                  <p><strong>Type:</strong> {scan?.scan_type}</p>
                  <p><strong>Location:</strong> {scan?.scan_location || 'Cloud'}</p>
                </div>
                <div className="gh-info-card">
                  <h4>Statistics</h4>
                  <div className="summary-stats">
                    <div className="summary-stat">
                      <span className="summary-label badge badge-high">High</span>
                      <span className="summary-value">{scan?.high_severity || 0}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="summary-label badge badge-medium">Medium</span>
                      <span className="summary-value">{scan?.medium_severity || 0}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="summary-label badge badge-low">Low</span>
                      <span className="summary-value">{scan?.low_severity || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="gh-panel">
            <div className="gh-panel-header">
              <h3 className="gh-panel-title">Exposure Narrative</h3>
            </div>
            <div className="gh-panel-body">
              {timelineLoading ? (
                <p>Loading timeline...</p>
              ) : timeline.length === 0 ? (
                <p className="gh-empty-state">No change detections recorded for this project.</p>
              ) : (
                <div className="gh-timeline">
                  {timeline.map((entry, idx) => (
                    <div key={idx} className="gh-timeline-item">
                      <div className="gh-timeline-badge"></div>
                      <div className="gh-timeline-content">
                        <div className="gh-timeline-header">
                          <span className="gh-timeline-title">
                            Diff between {formatTimelineWindow(entry)}
                          </span>
                        </div>
                        <div className="gh-timeline-body">
                          <div style={{ marginBottom: '10px' }}>
                            <strong style={{ fontSize: '12px', color: 'var(--gh-text-muted)' }}>CHANGES</strong>
                            <p style={{ margin: '5px 0' }}>{entry.what_changed || entry.change_summary}</p>
                          </div>
                          <div style={{ marginBottom: '10px' }}>
                            <strong style={{ fontSize: '12px', color: 'var(--gh-text-muted)' }}>IMPACT</strong>
                            <p style={{ margin: '5px 0' }}>{entry.why_it_matters || entry.impact}</p>
                          </div>
                          {entry.evidence_link || entry.evidence_url ? (
                            <a
                              href={entry.evidence_link || entry.evidence_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="gh-link"
                            >
                              View technical evidence →
                            </a>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'findings' && (
          <div className="gh-panel">
            <div className="gh-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="gh-panel-title">Vulnerability List</h3>
              <div className="filters" style={{ display: 'flex', gap: '10px' }}>
                <select
                  className="gh-select"
                  value={filters.severity}
                  onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                >
                  <option value="">All Severities</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <select
                  className="gh-select"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="false_positive">False Positive</option>
                  <option value="accepted_risk">Accepted Risk</option>
                  <option value="fixed">Fixed</option>
                </select>
              </div>
            </div>
            <div className="gh-panel-body">
              {isTrialSummary ? (
                <div className="gh-empty-state">
                  <h2 style={{ fontSize: '20px', marginBottom: '10px' }}>Trial Scan Summary Only</h2>
                  <p>Upgrade to view {findings.length} detailed findings, evidence snippets, and remediation steps.</p>
                  <a href="/pricing" className="gh-btn gh-btn-primary" style={{ marginTop: '15px', display: 'inline-block' }}>Upgrade Now</a>
                </div>
              ) : findings.length === 0 ? (
                <p className="gh-empty-state">No findings match your current filters.</p>
              ) : (
                <div className="gh-table-container">
                  <table className="gh-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Finding</th>
                        <th>Severity</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {findings.map((finding) => {
                        const networkInfo = parseNetworkFinding(finding);
                        return (
                          <React.Fragment key={finding.id}>
                            <tr className="gh-table-row">
                              <td><span className={`gh-badge gh-badge-outline`}>{finding.status || 'open'}</span></td>
                              <td>
                                <div style={{ fontWeight: 600 }}>{finding.title || finding.name}</div>
                                <div style={{ fontSize: '12px', color: 'var(--gh-text-muted)' }}>{finding.category}</div>
                              </td>
                              <td><span className={`badge badge-${finding.severity}`}>{finding.severity}</span></td>
                              <td>
                                <button className="gh-btn gh-btn-sm" onClick={() => {
                                  // Toggle expand logic could go here, or just show details below
                                }}>View Details</button>
                              </td>
                            </tr>
                            <tr>
                              <td colSpan="4" style={{ padding: '0 0 20px 0', borderTop: 'none' }}>
                                <div className="gh-finding-expanded">
                                  {networkInfo && renderNetworkFinding(finding, networkInfo)}
                                  <DeveloperFindingDetails finding={finding} />
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ScanDetail;
